type Scope = { identity: string; ready: boolean; recipientEmail?: string; service?:string; customerId?:string; boardId?:string; perspective?:'self'|'customers' };
type Dependencies = { baseUrl: () => string; scope: () => Scope; auth: () => Promise<string | null>; request?: typeof fetch };

/** One browser-tab context; handles and credentials stay in memory. */
export function createActionSessionClient(deps: Dependencies) {
  const request = deps.request ?? fetch;
  let clientId = crypto.randomUUID();
  let generation = 0;
  let cached: { token: string; expiresAt: number; scope: string; auth: string; base: string } | null = null;
  let pending: Promise<string | null> | null = null;
  let controller: AbortController | null = null;
  const scopeKey = () => JSON.stringify(deps.scope());
  const reset = () => {
    generation++; controller?.abort(); controller = null; pending = null;
    const previous = cached; const previousId = clientId;
    cached = null; clientId = crypto.randomUUID();
    if (previous) void request(`${previous.base}/v1/browser/sessions/revoke`, {
      method: 'POST', credentials: 'omit', keepalive: true, redirect: 'error',
      headers: { Authorization: `Bearer ${previous.auth}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ clientSessionId: previousId }),
    }).catch(() => undefined);
  };
  return {
    reset,
    async token(): Promise<string | null> {
      const base = deps.baseUrl().replace(/\/$/, '');
      const scope = deps.scope();
      if (!base || !scope.ready || !scope.identity) { if (cached) reset(); return null; }
      const key = scopeKey();
      if (cached && cached.scope !== key) reset();
      if (cached && cached.expiresAt > Date.now() + 60_000) return cached.token;
      if (pending) return pending;
      const current = generation;
      const currentId = clientId;
      const active = new AbortController(); controller = active;
      // The gateway may spend up to 15 seconds authorizing a cold service probe.
      const timer = setTimeout(() => active.abort(), 20_000);
      const work = async () => {
        try {
          const auth = await deps.auth();
          if (!auth || current !== generation || scopeKey() !== key) return null;
          const response = await request(`${base}/v1/browser/sessions`, {
            method: 'POST', credentials: 'omit', redirect: 'error', signal: active.signal,
            headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientSessionId: currentId, ...(scope.recipientEmail ? { recipientEmail: scope.recipientEmail } : {}),...(scope.service?{service:scope.service}:{}),...(scope.boardId?{boardId:scope.boardId}:{}),...(scope.customerId?{customerId:scope.customerId}:{}),...(scope.perspective?{perspective:scope.perspective}:{}) }),
          });
          if (!response.ok) return null;
          const result = await response.json() as { token?: unknown; expiresAt?: unknown };
          if (current !== generation || scopeKey() !== key || typeof result.token !== 'string' || !/^ast_[a-zA-Z0-9_-]{43}$/.test(result.token)
            || typeof result.expiresAt !== 'number' || result.expiresAt <= Date.now() || result.expiresAt > Date.now() + 305_000) return null;
          cached = { token: result.token, expiresAt: result.expiresAt, scope: key, auth, base };
          return cached.token;
        } catch { return null; }
        finally { clearTimeout(timer); if (current === generation) { pending = null; controller = null; } }
      };
      pending = work(); return pending;
    },
  };
}
